'use client';

import { motion, useInView } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = value;
      const duration = 2000;
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return (
    <div ref={ref} className="text-black text-4xl md:text-5xl mb-3 font-light tracking-wider">
      {count}{suffix}
    </div>
  );
}

const plans = {
  '5050': {
    title: '50 / 50',
    stats: [
      { number: 20, suffix: '%', label: 'Deposit + 4% DLD Fee + AED 5,250 Admin Fee' },
      { number: 5, suffix: '%', label: 'Every 3 Months' },
      { number: 50, suffix: '%', label: 'Upon Completion' },
    ],
  },
  '3070': {
    title: '30 / 70',
    stats: [
      { number: 20, suffix: '%', label: 'Deposit + 4% DLD Fee + AED 5,250 Admin Fee' },
      { number: 5, suffix: '%', label: 'Every 5 Months' },
      { number: 70, suffix: '%', label: 'Upon Completion' },
    ],
  },
} as const;

type PlanKey = keyof typeof plans;

export function PaymentPlanSection() {
  const [selected, setSelected] = useState<PlanKey>('3070');
  const plan = plans[selected];

  return (
    <section id="paymentplans" className="bg-white py-24 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-[1px] bg-black w-[80px] mx-auto mb-8 origin-center"
            />
            <h2 className="text-black tracking-[0.15em] font-light text-xl md:text-4xl mb-10">
              PAYMENT PLAN
            </h2>

            {/* Plan selector */}
            <div className="inline-flex border border-black/20">
              {(Object.keys(plans) as PlanKey[]).map((key) => (
                <button
                  key={key}
                  onClick={() => setSelected(key)}
                  className={`px-8 py-3 tracking-[0.2em] text-sm uppercase font-light transition-colors duration-300 ${
                    selected === key
                      ? 'bg-black text-white'
                      : 'bg-white text-black hover:bg-black/5'
                  }`}
                >
                  {plans[key].title}
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div
            key={selected}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-3 gap-8 md:gap-16"
          >
            {plan.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <AnimatedNumber value={stat.number} suffix={stat.suffix} />
                <div className="text-black/80 tracking-[0.15em] text-xs md:text-sm uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}